import React from 'react';
import { Badge } from 'react-bootstrap';

// ค่าคงที่และชิ้นส่วนหน้าจอที่ใช้ร่วมกันทั้งโมดูลห้องพยาบาล
// ตัวเลือกต้องตรงกับ ENUM ใน tb_nurse_visit (ดู migration 20260915_create_nurse_visit.sql)

export const OUTCOMES = [
  { value: 'back_to_class',  label: 'กลับเข้าเรียน',       icon: 'ri-arrow-go-back-line', variant: 'success' },
  { value: 'observe',        label: 'สังเกตอาการ',          icon: 'ri-eye-line',           variant: 'info' },
  { value: 'rest_here',      label: 'นอนพักที่ห้องพยาบาล',  icon: 'ri-hotel-bed-line',     variant: 'warning' },
  { value: 'send_home',      label: 'ส่งกลับบ้าน',          icon: 'ri-home-4-line',        variant: 'primary' },
  { value: 'refer_hospital', label: 'ส่งโรงพยาบาล',         icon: 'ri-hospital-line',      variant: 'danger' },
];

export const SOURCES = [
  { value: 'walk_in',      label: 'มาเอง' },
  { value: 'advisor_send', label: 'ครูส่งมา' },
  { value: 'dorm_night',   label: 'หอพัก/กลางคืน' },
  { value: 'activity',     label: 'ระหว่างกิจกรรม' },
  { value: 'follow_up',    label: 'มาตามนัด' },
];

// ผลลัพธ์ที่แปลว่าไม่ได้อยู่ในห้องเรียน — ระบบจะแจ้งลาป่วยให้เอง (server เป็นคนตัดสิน
// ค่าตรงนี้ใช้แค่บอกผู้ใช้ล่วงหน้าว่าจะเกิดอะไรขึ้น)
export const LEAVE_OUTCOMES = ['rest_here', 'send_home', 'refer_hospital'];
// ผลลัพธ์ที่ออกนอกโรงเรียน — เปิดส่วนออกใบผ่านประตู
export const EXIT_OUTCOMES = ['send_home', 'refer_hospital'];

export const outcomeMeta = (value) =>
  OUTCOMES.find(o => o.value === value) || { label: value || '-', variant: 'secondary', icon: '' };

export const outcomeBadge = (value) => {
  const m = outcomeMeta(value);
  return <Badge bg={m.variant}>{m.label}</Badge>;
};

export const sourceLabel = (value) =>
  SOURCES.find(s => s.value === value)?.label || value || '-';

// ── คลังยา (เฟส 2) ────────────────────────────────────────────────────────
// ต้องตรงกับ ENUM ใน tb_nurse_med_item / tb_nurse_med_lot
export const MED_FORMS = [
  { value: 'tablet',    label: 'เม็ด/แคปซูล' },
  { value: 'syrup',     label: 'ยาน้ำ' },
  { value: 'cream',     label: 'ครีม/ขี้ผึ้ง' },
  { value: 'drops',     label: 'ยาหยอด' },
  { value: 'injection', label: 'ยาฉีด' },
  { value: 'supply',    label: 'เวชภัณฑ์' },
];

export const MED_SOURCES = [
  { value: 'purchase',      label: 'จัดซื้อ' },
  { value: 'donation',      label: 'รับบริจาค' },
  { value: 'health_office', label: 'รพ.สต./สาธารณสุข' },
  { value: 'other',         label: 'อื่นๆ' },
];

export const formLabel = (v) => MED_FORMS.find(f => f.value === v)?.label || v || '-';

// ที่มาของ "ล็อตยา" — คนละชุดกับ sourceLabel ที่บอกว่านักเรียนมาห้องพยาบาลอย่างไร
export const medSourceLabel = (v) => MED_SOURCES.find(s => s.value === v)?.label || v || '-';

export const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const nowHHMM = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const thaiDate = (value) => (
  value ? new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'
);

// 'YYYY-MM-DD HH:mm' จาก server — ตัดเอาเฉพาะเวลา ไม่แปลงผ่าน Date เพราะสตริงนี้
// เป็นเวลาท้องถิ่นอยู่แล้ว การให้ Date ตีความจะเลื่อนไปตามโซนเวลาของเบราว์เซอร์
export const timeOf = (value) => (value ? String(value).slice(11, 16) : '');

export const dateOf = (value) => (value ? String(value).slice(0, 10) : '');

// ระยะเวลาที่อยู่ในห้องพยาบาล — ค้างคืนต้องอ่านออกด้วย
export const stayText = (checkIn, checkOut) => {
  if (!checkIn) return '';
  const start = new Date(String(checkIn).replace(' ', 'T'));
  const end = checkOut ? new Date(String(checkOut).replace(' ', 'T')) : new Date();
  const mins = Math.max(0, Math.round((end - start) / 60000));
  if (mins < 60) return `${mins} นาที`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h} ชม. ${m} นาที` : `${h} ชม.`;
  return `${Math.floor(h / 24)} วัน ${h % 24} ชม.`;
};

// สิ่งแรกที่ต้องเห็นก่อนจ่ายยา — ค่าพวกนี้อยู่ใน tb_student ตั้งแต่ตอนรับสมัคร
// "ไม่มี" / "-" คือคำตอบที่ผู้ปกครองกรอกไว้จริง ไม่ใช่ช่องว่าง จึงไม่ต้องเตือน
const isBlank = (v) => {
  const s = String(v ?? '').trim();
  return !s || s === '-' || s === 'ไม่มี' || s === 'ไม่ม' || s === 'ไม่';
};

export const HealthAlert = ({ student, className = '' }) => {
  if (!student) return null;
  const items = [
    { label: 'แพ้ยา', value: student.st_drug, variant: 'danger' },
    { label: 'แพ้อาหาร', value: student.st_food, variant: 'warning' },
    { label: 'โรคประจำตัว', value: student.st_syndrome, variant: 'danger' },
    { label: 'วิธีแก้ไขเมื่อเจ็บป่วย', value: student.st_facingSyndrome, variant: 'info' },
  ].filter(i => !isBlank(i.value));

  if (items.length === 0) {
    return (
      <div className={`alert alert-light border py-2 fs-sm mb-3 ${className}`}>
        <i className="ri-information-line me-1"></i>
        ไม่มีข้อมูลการแพ้ยา/โรคประจำตัวในระเบียน — ถามนักเรียนก่อนจ่ายยาทุกครั้ง
      </div>
    );
  }

  return (
    <div className={`alert alert-danger py-2 mb-3 ${className}`}>
      <div className="fw-semibold mb-1">
        <i className="ri-alarm-warning-line me-1"></i>ข้อมูลสุขภาพที่ต้องรู้ก่อนจ่ายยา
      </div>
      <ul className="mb-0 ps-3 fs-sm">
        {items.map(i => (
          <li key={i.label}><span className="fw-semibold">{i.label}:</span> {i.value}</li>
        ))}
      </ul>
    </div>
  );
};

// ธงเล็กในตาราง/รายการ — ไม่แสดงเนื้อหา แค่บอกว่ามีให้ไปดู
export const HealthFlags = ({ student }) => {
  const flags = [];
  if (!isBlank(student?.st_drug)) flags.push({ text: 'แพ้ยา', variant: 'danger' });
  if (!isBlank(student?.st_syndrome)) flags.push({ text: 'โรคประจำตัว', variant: 'warning' });
  if (flags.length === 0) return null;
  return (
    <>
      {flags.map(f => (
        <Badge key={f.text} bg={f.variant} className="ms-1 fw-normal">{f.text}</Badge>
      ))}
    </>
  );
};
